// Suite for managing relevant ChatBLK cached user data
import AcwClientSideStorage, {
    AcwClientSideDB,
} from '@blk/acw-client-side-storage';
import { CHAT_BLK } from '../constants';
import { ChatBlkAppContext } from '../types';

/**
 * Returns the session storage key for the given data.
 * @param data - The data to create the key for.
 * @returns {string}
 */
export function getStorageKey(data = ''): string {
    const chatblk = CHAT_BLK.toLowerCase();
    return data?.startsWith(chatblk) ? data : `${chatblk}_${data}`;
}

/**
 * Clears the chat cache.
 * @param items - The keys of the items to clear from the cache.
 */
export function clearChatCache(items: Array<string>): void {
    items?.forEach((item) => deleteCacheValue(getStorageKey(item) || ''));
}

export function deleteCacheValue(key: string, isLocalStorage = false): void {
    (isLocalStorage ? localStorage : sessionStorage).removeItem(
        getStorageKey(key)
    );
}

/**
 * Sets the value for a sessionStorage item.
 * @param key - string representing the key to set the value of the sessionStorage item.
 * @param value - string representing the value to set for the sessionStorage item.
 */
export function setCacheValue(
    key: string,
    value: string,
    isLocalStorage = false
): void {
    (isLocalStorage ? localStorage : sessionStorage).setItem(
        getStorageKey(key) || '',
        value ? JSON.stringify(value) : ''
    );
}

/**
 * Gets the value to sessionStorage item.
 * @param key - string representing the key to set the value of the sessionStorage item.
 * @returns {string}
 */
export function getCacheValue(
    key = '',
    isLocalStorage = false
): string | boolean | object {
    if (key) {
        const item = (isLocalStorage ? localStorage : sessionStorage).getItem(
            getStorageKey(key)
        );
        const value = item ? JSON.parse(item) : '';
        if (['true', 'false'].includes(value)) {
            return 'true' === value;
        } else {
            return value || '';
        }
    }
    return '';
}

export const resetChatImageStorage = async (appContext: ChatBlkAppContext) => {
    await getStore(appContext)?.deleteStore();
};

export const getStore = (
    appContext: ChatBlkAppContext
): AcwClientSideDB | null => {
    const appName = appContext?.aiProps?.appName;
    if (!appName) return null;

    return AcwClientSideStorage.getInstance().db({
        name: appName,
        storeName: `${appName}__CHAT_IMAGES`,
    });
};
